import React, { useEffect, useState } from "react";
import {
  getApplications,
  updateApplicationStatus,
  deleteApplication,
  getResumeUrl,
} from "../services/applicationsService";
import { isSupabaseConfigured } from "../supabase/client";

const STATUSES = ["new", "reviewing", "shortlisted", "rejected"];
const STATUS_LABEL = {
  new: "New",
  reviewing: "Reviewing",
  shortlisted: "Shortlisted",
  rejected: "Rejected",
};

const ApplicationsAdmin = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      setApps(await getApplications());
    } catch (e) {
      setError("Couldn't load applications. Check Supabase config / rules.");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isSupabaseConfigured) load();
    else setLoading(false);
  }, []);

  const changeStatus = async (id, status) => {
    setApps((a) => a.map((x) => (x.id === id ? { ...x, status } : x))); // optimistic
    try {
      await updateApplicationStatus(id, status);
    } catch (e) {
      load();
    }
  };

  const handleDelete = async (app) => {
    if (!window.confirm(`Delete ${app.fullName}'s application? This can't be undone.`)) return;
    await deleteApplication(app.id);
    await load();
  };

  const openResume = async (path) => {
    try {
      const url = await getResumeUrl(path);
      if (url) window.open(url, "_blank", "noopener");
    } catch (e) {
      alert("Couldn't open the resume.");
    }
  };

  // Free-text search across the fields an admin would look someone up by.
  const SEARCH_FIELDS = [
    "fullName", "email", "phone", "city",
    "jobTitle", "jobDepartment",
    "institution", "degree", "fieldOfStudy", "graduationYear", "gpa",
    "matric", "intermediate", "experienceYears", "coverNote",
  ];
  const matches = (a, terms) =>
    terms.every((t) =>
      SEARCH_FIELDS.some((f) => String(a[f] || "").toLowerCase().includes(t))
    );

  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const shown = apps
    .filter((a) => (filter === "all" ? true : (a.status || "new") === filter))
    .filter((a) => (terms.length ? matches(a, terms) : true));
  const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "");

  return (
    <>
      <div className="admin-topbar">
        <h2>Applications</h2>
      </div>

      <div className="admin-content">
        {!isSupabaseConfigured && (
          <div className="config-warning">
            Add your Supabase keys to <code>.env</code> and run <code>applications.sql</code> to enable this.
          </div>
        )}
        {error && <div className="admin-alert error">{error}</div>}

        <div className="app-search">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, university, degree, position, email…"
          />
          {query && (
            <button className="app-search-clear" onClick={() => setQuery("")} title="Clear search">×</button>
          )}
        </div>

        <div className="app-filters">
          {["all", ...STATUSES].map((s) => (
            <button
              key={s}
              className={`app-filter ${filter === s ? "active" : ""}`}
              onClick={() => setFilter(s)}
            >
              {s === "all" ? "All" : STATUS_LABEL[s]}
              {s === "all" && ` (${apps.length})`}
            </button>
          ))}
          {(terms.length > 0 || filter !== "all") && (
            <span className="app-result-count">
              {shown.length} result{shown.length === 1 ? "" : "s"}
            </span>
          )}
        </div>

        <div className="admin-panel">
          {loading ? (
            <div className="admin-empty">Loading…</div>
          ) : shown.length === 0 ? (
            <div className="admin-empty">
              {terms.length
                ? `No applications match "${query.trim()}".`
                : `No applications${filter !== "all" ? ` marked "${STATUS_LABEL[filter]}"` : " yet"}.`}
            </div>
          ) : (
            <ul className="admin-list">
              {shown.map((a) => (
                <li className="app-item" key={a.id}>
                  <div className="app-row" onClick={() => setOpenId(openId === a.id ? null : a.id)}>
                    <div className="app-main">
                      <p className="app-name">
                        {a.fullName}
                        <span className={`app-status-dot s-${a.status || "new"}`} />
                      </p>
                      <p className="app-sub">
                        {a.jobTitle || "—"}{a.jobDepartment ? ` · ${a.jobDepartment}` : ""} &nbsp;•&nbsp; {a.email}{a.phone ? ` · ${a.phone}` : ""}
                      </p>
                    </div>
                    <div className="app-row-right">
                      <span className="app-date">{fmtDate(a.createdAt)}</span>
                      <span className="app-expand">{openId === a.id ? "▲" : "▼"}</span>
                    </div>
                  </div>

                  {openId === a.id && (
                    <div className="app-detail">
                      <div className="app-detail-grid">
                        <div><span>City</span>{a.city || "—"}</div>
                        <div><span>Experience</span>{a.experienceYears ? `${a.experienceYears} yr` : "—"}</div>
                        <div><span>Matric / O-Level</span>{a.matric || "—"}</div>
                        <div><span>Inter / A-Level</span>{a.intermediate || "—"}</div>
                        <div><span>Degree</span>{a.degree || "—"}</div>
                        <div><span>Field of study</span>{a.fieldOfStudy || "—"}</div>
                        <div><span>Institution</span>{a.institution || "—"}</div>
                        <div><span>Graduation</span>{a.graduationYear || "—"}</div>
                        <div><span>GPA / CGPA</span>{a.gpa || "—"}</div>
                        <div><span>LinkedIn</span>{a.linkedinUrl ? <a href={a.linkedinUrl} target="_blank" rel="noopener noreferrer">Profile ↗</a> : "—"}</div>
                      </div>
                      {a.coverNote && (
                        <div className="app-cover">
                          <span>Cover note</span>
                          <p>{a.coverNote}</p>
                        </div>
                      )}
                      <div className="app-actions">
                        {a.resumePath && (
                          <button className="btn btn-navy" onClick={() => openResume(a.resumePath)}>Open resume</button>
                        )}
                        <label className="app-status-select">
                          Status:
                          <select value={a.status || "new"} onChange={(e) => changeStatus(a.id, e.target.value)}>
                            {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                          </select>
                        </label>
                        <a className="btn btn-ghost" href={`mailto:${a.email}`}>Email applicant</a>
                        <button className="btn btn-danger-text" onClick={() => handleDelete(a)}>Delete</button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
};

export default ApplicationsAdmin;
